import i18n from "../../../i18n";import { Trans } from "react-i18next";import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChevronDown,
  Check,
  Search,
  X } from
'lucide-react';
import { AppLayout } from '../../../components/layout';
import { PageBackLink } from '../../../components/layout/PageBackLink';
import { PageHeader, ui } from '../../../components/ui';
// import { VoiceMicButton } from '../../../components/voice/VoiceMicButton';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../../../components/ui/select';
import {
  searchCustomers,
  searchCustomerLocations,
  searchCustomerContacts,
  searchProductServices,
  searchComplaintsByProduct,
  searchCustomerAssets,
  buildCreateComplaintPayload,
  createCustomerComplaint,
  formatCallTypeLabel } from
'../../../services/fsmService';
import { translateTicketPriority } from '../data/ticketConstants';
import { CRM_ONBOARDING_ADD_PATH } from '../../crm/routes';

const COMPLAINT_MODES = ["Email", "Phone", "WhatsApp"];
const WARRANTY_STATUSES = ["Yes", "No"];
const AMC_STATUSES = ["Comprehensive", "Non_Comprehensive", "No_AMC"];
const CALL_TYPES = ["Service_Call", "Breakdown_Call"];
const PRIORITIES = ["Critical", "High", "Medium", "Low"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const getNowDateTimeLocal = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

function mergeUniqueById(existing, incoming) {
  const map = new Map();
  [...(existing || []), ...(incoming || [])].forEach((item) => {
    if (item?.id == null || item.id === '') return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
}

// ---------------------------------------------------------------------------
// SearchableDropdown — reusable inline component
// Matches the design of the existing customer dropdown exactly.
// ---------------------------------------------------------------------------

function SearchableDropdown({
  label,
  required,
  placeholder,
  searchPlaceholder,
  items, // [{ id, label }]
  selectedId,
  onSelect, // (item) => void
  loading,
  error,
  disabled,
  hasError,
  createLabel, // e.g. '+ Create New Customer'
  onCreateClick,
  hasMore = false,
  loadingMore = false,
  onLoadMore
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef(null);
  const listRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(q));
  }, [items, search]);

  const selected = useMemo(
    () => items.find((item) => item.id === selectedId) ?? null,
    [items, selectedId]
  );

  function handleToggle() {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (!isOpen) setSearch('');
  }

  function handleSelect(item) {
    onSelect(item);
    setIsOpen(false);
    setSearch('');
  }

  function handleListScroll(e) {
    if (!onLoadMore || !hasMore || loadingMore || loading) return;
    // Only paginate the full list — local search is already a subset
    if (search.trim()) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 32) {
      onLoadMore();
    }
  }

  const triggerLabel = loading && !selected ?
  `Loading ${label.toLowerCase()}...` :
  selected ?
  selected.label :
  placeholder;

  return (
    <div className="flex flex-col relative" ref={wrapperRef}>
      {/* Trigger */}
      <div
        onClick={handleToggle}
        className={cn(
          'h-10 px-3 w-full border rounded-xl text-sm bg-white flex items-center justify-between transition',
          disabled ?
          'bg-gray-50 text-gray-400 cursor-not-allowed border-[var(--color-text-border)]' :
          'cursor-pointer select-none',
          hasError ?
          'border-[var(--color-error)]' :
          'border-[var(--color-text-border)]'
        )}>
        
        <span className={cn('truncate', !selected && 'text-gray-400')}>
          {triggerLabel}
        </span>
        <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
      </div>

      {/* Popover */}
      {isOpen && !disabled &&
      <div className="absolute top-[42px] left-0 z-50 w-full bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-1 duration-150">
          {/* Search box */}
          <div className="p-2 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
            <Search className="h-4 w-4 text-gray-400 shrink-0" />
            <input
            type="text"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-transparent border-none outline-none text-xs w-full h-7 py-1"
            autoFocus />
          
          </div>

          {/* List */}
          <div
          ref={listRef}
          className="max-h-52 overflow-y-auto"
          onScroll={handleListScroll}>
          
            {loading && items.length === 0 ?
          <div className="px-4 py-3 text-xs text-gray-400 text-center">
                <Trans i18nKey="common.loading" />
              </div> :
          error && items.length === 0 ?
          <div className="px-4 py-3 text-xs text-[var(--color-error)] text-center">
                {error}
              </div> :
          filtered.length === 0 ?
          <div className="px-4 py-3 text-xs text-gray-400 text-center">
                <Trans i18nKey="fsm.noResultsFound" />
              </div> :

          <>
                {filtered.map((item) =>
            <div
              key={item.id}
              onClick={() => handleSelect(item)}
              className={cn(
                'px-4 py-2.5 text-xs font-semibold text-foreground hover:bg-[var(--color-workspace)] cursor-pointer flex items-center justify-between transition-colors',
                selectedId === item.id && 'bg-[var(--color-workspace)] text-[var(--color-primary)]'
              )}>
              
                    <span className="truncate">{item.label}</span>
                    {selectedId === item.id &&
              <Check className="h-3.5 w-3.5 text-[var(--color-primary)] shrink-0" />
              }
                  </div>
            )}
                {loadingMore ?
            <div className="px-4 py-2 text-[11px] text-gray-400 text-center">
                    <Trans i18nKey="fsm.loadingMore" />
                  </div> :
            null}
              </>
          }
          </div>

          {/* Create New footer */}
          {createLabel &&
        <div
          onClick={() => {
            onCreateClick?.();
            setIsOpen(false);
          }}
          className="border-t border-gray-100 px-4 py-2.5 text-xs font-bold text-[var(--color-accent)] hover:bg-gray-50 cursor-pointer transition-colors text-center select-none">
          
              {createLabel}
            </div>
        }
        </div>
      }
    </div>);

}

// ---------------------------------------------------------------------------
// FsmCreateTicketPage
// ---------------------------------------------------------------------------

export function FsmCreateTicketPage() {
  const navigate = useNavigate();

  // ── Customer ──────────────────────────────────────────────────────────────
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customersLoadingMore, setCustomersLoadingMore] = useState(false);
  const [customersError, setCustomersError] = useState('');
  const [customersPage, setCustomersPage] = useState(0);
  const [customersHasMore, setCustomersHasMore] = useState(false);
  const customersFetchLockRef = useRef(false);

  // ── Location ──────────────────────────────────────────────────────────────
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState('');

  // ── Point of Contact ──────────────────────────────────────────────────────
  const [selectedContact, setSelectedContact] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contactsError, setContactsError] = useState('');

  // ── Other form fields ─────────────────────────────────────────────────────
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [complaintMode, setComplaintMode] = useState('');
  const [complaintDate, setComplaintDate] = useState(getNowDateTimeLocal);

  // ── System (products-services) ────────────────────────────────────────────
  const [systems, setSystems] = useState([]);
  const [systemsLoading, setSystemsLoading] = useState(false);
  const [systemsError, setSystemsError] = useState('');
  const [selectedSystem, setSelectedSystem] = useState(null);

  // ── Complaint types (by system) ───────────────────────────────────────────
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [complaintsLoading, setComplaintsLoading] = useState(false);
  const [complaintsError, setComplaintsError] = useState('');
  const [selectedComplaint, setSelectedComplaint] = useState(null);

  const [, setSelectedTemplateId] = useState('');
  const [customComplaintType, setCustomComplaintType] = useState('');
  const [priority, setPriority] = useState('');
  const [description, setDescription] = useState('');

  // ── Products / assets ─────────────────────────────────────────────────────
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ── Warranty, AMC & Call Type ─────────────────────────────────────────────
  const [warrantyType, setWarrantyType] = useState('');
  const [amcType, setAmcType] = useState('');
  const [callType, setCallType] = useState('');

  // ── UI feedback ───────────────────────────────────────────────────────────
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // ── Derived items lists for dropdowns ─────────────────────────────────────
  const customerItems = useMemo(() => customers.map((c) => ({ id: c.id, label: c.name })), [customers]);
  const locationItems = useMemo(() => locations.map((l) => ({ id: l.id, label: l.label })), [locations]);
  const contactItems = useMemo(() => contacts.map((co) => ({ id: co.id, label: co.name })), [contacts]);

  const selectedComplaintName = selectedComplaint?.name ?? '';
  const isOthersComplaint = selectedComplaintName === 'Others';

  const loadCustomersPage = useCallback(async (page, { append = false } = {}) => {
    if (customersFetchLockRef.current) return;
    customersFetchLockRef.current = true;

    if (append) setCustomersLoadingMore(true);else
    {
      setCustomersLoading(true);
      setCustomersError('');
    }

    try {
      const result = await searchCustomers({
        page,
        sortBy: 'customerId',
        sortDir: 'ASC'
      });
      setCustomers((prev) =>
      append ? mergeUniqueById(prev, result.items) : result.items
      );
      setCustomersPage(result.page);
      setCustomersHasMore(Boolean(result.hasMore));
    } catch (err) {
      console.error('Error loading customers:', err);
      if (!append) setCustomers([]);
      setCustomersError(
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        'Failed to load customers.'
      );
    } finally {
      customersFetchLockRef.current = false;
      setCustomersLoading(false);
      setCustomersLoadingMore(false);
    }
  }, []);

  const handleLoadMoreCustomers = useCallback(() => {
    if (!customersHasMore || customersLoadingMore || customersLoading) return;
    loadCustomersPage(customersPage + 1, { append: true });
  }, [
  customersHasMore,
  customersLoadingMore,
  customersLoading,
  customersPage,
  loadCustomersPage]
  );

  // ── Load customers + systems on mount ────────────────────────────────────
  useEffect(() => {
    loadCustomersPage(0, { append: false });
  }, [loadCustomersPage]);

  useEffect(() => {
    let cancelled = false;
    setSystemsError('');
    setSystemsLoading(true);

    searchProductServices({ page: 0, size: 10, sortBy: 'productServiceId', sortDir: 'ASC' })
    .then((data) => {if (!cancelled) setSystems(data);})
    .catch((err) => {
      console.error('Error loading systems:', err);
      if (!cancelled) {
        setSystems([]);
        setSystemsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          'Failed to load systems.'
        );
      }
    })
    .finally(() => {if (!cancelled) setSystemsLoading(false);});

    return () => {cancelled = true;};
  }, []);

  // ── Load locations when customer changes ──────────────────────────────────
  useEffect(() => {
    setSelectedLocation(null);
    setLocations([]);
    setLocationsError('');
    setSelectedContact(null);
    setContacts([]);
    setContactsError('');
    setContact('');
    setEmail('');
    setSelectedProduct(null);
    setProducts([]);
    setProductsError('');


    if (!selectedCustomer) return;

    let cancelled = false;
    setLocationsLoading(true);

    searchCustomerLocations({
      customerId: selectedCustomer.customerId,
      page: 0, size: 10, sortBy: 'locationId', sortDir: 'ASC'
    })
    .then((data) => {if (!cancelled) setLocations(data);})
    .catch((err) => {
      console.error('Error loading locations:', err);
      if (!cancelled) {
        setLocations([]);
        setLocationsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          'Failed to load locations.'
        );
      }
    })
    .finally(() => {if (!cancelled) setLocationsLoading(false);});

    return () => {cancelled = true;};
  }, [selectedCustomer]);

  // ── Load contacts when customer changes ───────────────────────────────────
  useEffect(() => {
    if (!selectedCustomer) return;

    let cancelled = false;
    setContactsLoading(true);

    searchCustomerContacts({
      customerId: selectedCustomer.customerId,
      page: 0, size: 10, sortBy: 'contactId', sortDir: 'ASC'
    })
    .then((data) => {if (!cancelled) setContacts(data);})
    .catch((err) => {
      console.error('Error loading contacts:', err);
      if (!cancelled) {
        setContacts([]);
        setContactsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          'Failed to load contacts.'
        );
      }
    })
    .finally(() => {if (!cancelled) setContactsLoading(false);});

    return () => {cancelled = true;};
  }, [selectedCustomer]);

  // ── Load products/assets when customer + location are selected ────────────
  useEffect(() => {
    setSelectedProduct(null);
    setProducts([]);
    setProductsError('');

    if (!selectedCustomer || !selectedLocation) return;

    let cancelled = false;
    setProductsLoading(true);

    searchCustomerAssets({
      customerId: selectedCustomer.customerId ?? selectedCustomer.id,
      locationId: selectedLocation.locationId ?? selectedLocation.id,
      page: 0,
      size: 10,
      sortBy: 'assetId',
      sortDir: 'ASC'
    })
    .then((data) => {
      if (!cancelled) setProducts(data);
    })
    .catch((err) => {
      console.error('Error loading products:', err);
      if (!cancelled) {
        setProducts([]);
        setProductsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          'Failed to load products.'
        );
      }
    })
    .finally(() => {
      if (!cancelled) setProductsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedCustomer, selectedLocation]);

  // ── Load complaints when system changes ───────────────────────────────────
  useEffect(() => {
    setSelectedComplaint(null);
    setComplaintTypes([]);
    setComplaintsError('');
    setCustomComplaintType('');
    setSelectedTemplateId('');

    if (!selectedSystem) return;

    let cancelled = false;
    setComplaintsLoading(true);

    searchComplaintsByProduct({
      productServiceId: selectedSystem.productServiceId,
      page: 0, size: 10, sortBy: 'complaintTypeId', sortDir: 'ASC'
    })
    .then((data) => {if (!cancelled) setComplaintTypes(data);})
    .catch((err) => {
      console.error('Error loading complaint types:', err);
      if (!cancelled) {
        setComplaintTypes([]);
        setComplaintsError(
          err.status === 401 ?
          'Session expired. Please refresh and try again.' :
          'Failed to load complaint types.'
        );
      }
    })
    .finally(() => {if (!cancelled) setComplaintsLoading(false);});

    return () => {cancelled = true;};
  }, [selectedSystem]);

  // ── Auto-select template when system + complaint match ────────────────────
  // useEffect(() => {
  //   if (selectedSystem && selectedComplaint) {
  //     const match = MOCK_TEMPLATES_LIST.find(
  //       (t) => t.system === selectedSystem.name && t.complaintType === selectedComplaint.name
  //     );
  //     if (match) setSelectedTemplateId(match.id);
  //   }
  // }, [selectedSystem, selectedComplaint]);
  // useEffect(() => {
  //   console.log("Selected Customer:", selectedCustomer)
  // }, [selectedCustomer]);
  // ── Validation ────────────────────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};
    if (!selectedCustomer) newErrors.customer = 'Customer name is required';
    if (!selectedLocation) newErrors.location = 'Complaint location is required';
    if (!complaintMode) newErrors.complaintMode = 'Complaint mode is required';
    if (!complaintDate) newErrors.complaintDate = 'Complaint date is required';
    if (!selectedSystem) newErrors.system = 'System is required';
    if (!selectedComplaint) newErrors.complaintType = 'Type of complaint is required';
    if (isOthersComplaint && !customComplaintType.trim()) {
      newErrors.customComplaintType = 'Custom complaint type is required';
    }
    if (!priority) newErrors.priority = 'Priority is required';
    if (!String(description || '').trim()) newErrors.description = 'Detailed description is required';
    if (!selectedContact) newErrors.pointOfContact = 'Point of contact is required';
    if (!String(contact || '').trim()) {
      newErrors.contact = 'Contact number is required';
    } else if (!/^\d{10,15}$/.test(String(contact).trim())) {
      newErrors.contact = 'Enter a valid contact number (10–15 digits).';
    }
    if (!String(email || '').trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim())) {
      newErrors.email = 'Enter a valid email address.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setToast({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    if (submitting) return;

    setSubmitting(true);
    try {
      const payload = buildCreateComplaintPayload({
        customer: selectedCustomer,
        system: selectedSystem,
        complaint: selectedComplaint,
        product: selectedProduct,
        location: selectedLocation,
        warrantyType,
        amcType,
        callType,
        priority,
        pointOfContact: selectedContact?.name ?? '',
        contactNumber: contact,
        email,
        complaintMode,
        complaintRecievedOn: complaintDate || '',
        complaintDescription: description
      });

      const result = await createCustomerComplaint(payload);
      const complaintNo =
      result?.complaintNumber ??
      result?.complaintNo ??
      result?.ticketNumber ??
      '';

      setToast({
        type: 'success',
        message: complaintNo ?
        `Ticket ${complaintNo} created successfully!` :
        'Ticket created successfully!'
      });
      setTimeout(() => navigate('/fsm/customer-complaints'), 1500);
    } catch (err) {
      console.error('Error creating ticket:', err);
      setToast({
        type: 'error',
        message:
        err.status === 401 ?
        'Session expired. Please refresh and try again.' :
        err.message || 'Failed to create ticket.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate('/fsm/customer-complaints');

  // ── Priority badge ────────────────────────────────────────────────────────
  // ── Shared style tokens ───────────────────────────────────────────────────
  const sectionCard = 'bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.22)] border border-[var(--color-text-border)]/60 p-6 mb-6';
  const labelCls = 'text-[13px] font-semibold text-[#374151] mb-1.5 block';
  const inputCls = (hasError) => cn(
    'h-10 px-3 w-full border rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/15 transition',
    hasError ?
    'border-[var(--color-error)] focus:border-[var(--color-error)]' :
    'border-[var(--color-text-border)] focus:border-[var(--color-accent)]'
  );

  // =========================================================================
  return (
    <AppLayout>
      {/* Toast */}
      {toast &&
      <div className={cn(
        'fixed bottom-6 right-6 z-50 p-4 rounded-xl shadow-lg flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300',
        toast.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
      )}>
          <span className="text-sm font-semibold">{toast.message}</span>
          <button onClick={() => setToast(null)} className="hover:opacity-85 text-white/90">
            <X className="h-4 w-4" />
          </button>
        </div>
      }

      {/* Header */}
      <div className="sticky top-[-24px] lg:top-[-28px] z-20 -mt-2 mb-6 bg-[var(--color-workspace)] pt-2 pb-3">
        <PageBackLink to="/fsm/customer-complaints" />
        <PageHeader
          title={i18n.t("formActions.createTicket")}
          // subtitle="Log a new customer complaint"
          className="mb-0 mt-2" />
      </div>

      <form onSubmit={handleSubmit} className="max-w-4xl">

        {/* ── SECTION 1: Customer Information ─────────────────────────────── */}
        <div className={sectionCard}>
          <div className="flex items-center gap-3 mb-5 border-b border-gray-100 pb-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-workspace)] text-[var(--color-primary)] font-bold text-xs">
              1
            </span>
            <h2 className="text-[15px] font-bold text-foreground"><Trans i18nKey="fsm.customerInformation" /></h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

            {/* Customer name */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="inventory.customerName" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <SearchableDropdown
                label="customers"
                placeholder={i18n.t("fsm.selectACustomer")}
                searchPlaceholder="Search customer..."
                items={customerItems}
                selectedId={selectedCustomer?.id ?? ''}
                onSelect={(item) => {
                  const full = customers.find((c) => c.id === item.id) ?? null;
                  setSelectedCustomer(full);
                }}
                loading={customersLoading}
                error={customersError}
                disabled={false}
                hasError={!!errors.customer}
                createLabel="+ Create New Customer"
                onCreateClick={() => navigate(CRM_ONBOARDING_ADD_PATH)}
                hasMore={customersHasMore}
                loadingMore={customersLoadingMore}
                onLoadMore={handleLoadMoreCustomers} />
              
              {errors.customer &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.customer}</p>
              }
            </div>

            {/* Customer priority */}
            <div className="flex flex-col">
              
              <label className={labelCls}><Trans i18nKey="common.accountType" /></label>
              <div className="h-10 flex items-center">
                {selectedCustomer ?
                <span className={cn(
                  'inline-flex items-center px-3 py-1 rounded-full text-xs font-bold border'
                  // getPriorityBadgeClass(selectedCustomer.accountType)
                )}>
                    {selectedCustomer.raw.accountType}
                  </span> :

                <span className="text-xs text-gray-400 italic">
                    <Trans i18nKey="fsm.pulledFromCustomerRecords" />
                  </span>
                }
              </div>
            </div>

            {/* Complaint location / address */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.complaintLocationAddress" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <SearchableDropdown
                label="locations"
                placeholder={selectedCustomer ? 'Select a location' : 'Choose a customer first'}
                searchPlaceholder="Search location..."
                items={locationItems}
                selectedId={selectedLocation?.id ?? ''}
                onSelect={(item) => {
                  const full = locations.find((l) => l.id === item.id) ?? null;
                  setSelectedLocation(full);
                }}
                loading={locationsLoading}
                error={locationsError}
                disabled={!selectedCustomer}
                hasError={!!errors.location}
                createLabel="+ Create New Location"
                onCreateClick={() => navigate(CRM_ONBOARDING_ADD_PATH)} />
              
              {errors.location &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.location}</p>
              }
            </div>

            {/* Point of contact */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.pointOfContact" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <SearchableDropdown
                label="contacts"
                placeholder={selectedCustomer ? 'Select a contact' : 'Choose a customer first'}
                searchPlaceholder="Search contact..."
                items={contactItems}
                selectedId={selectedContact?.id ?? ''}
                onSelect={(item) => {
                  const full = contacts.find((c) => c.id === item.id) ?? null;
                  setSelectedContact(full);
                  setContact(full?.phone ?? '');
                  setEmail(full?.email ?? '');
                }}
                loading={contactsLoading}
                error={contactsError}
                disabled={!selectedCustomer}
                hasError={!!errors.pointOfContact}
                createLabel="+ Create New Contact"
                onCreateClick={() => navigate(CRM_ONBOARDING_ADD_PATH)} />
              
              {errors.pointOfContact &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.pointOfContact}</p>
              }
            </div>

            {/* Contact number */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.contactNumber" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                type="text"
                pattern="[0-9]*"
                placeholder={i18n.t("fsm.10DigitMobile")}
                value={contact}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^[0-9\b]+$/.test(val)) setContact(val);
                }}
                className={inputCls(!!errors.contact)} />
              
              {errors.contact &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.contact}</p>
              }
            </div>

            {/* Email */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="common.email" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                type="email"
                placeholder={i18n.t("fsm.nameCompanyIn")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls(!!errors.email)} />
              
              {errors.email &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.email}</p>
              }
            </div>

          </div>
        </div>

        {/* ── SECTION 2: Complaint Information ────────────────────────────── */}
        <div className={sectionCard}>
          <div className="flex items-center gap-3 mb-5 border-b border-gray-100 pb-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-workspace)] text-[var(--color-primary)] font-bold text-xs">
              2
            </span>
            <h2 className="text-[15px] font-bold text-foreground"><Trans i18nKey="fsm.complaintInformation" /></h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">

            {/* Complaint mode */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.complaintMode" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select value={complaintMode} onValueChange={setComplaintMode}>
                <SelectTrigger
                  className="h-10 w-full justify-start rounded-xl border-[var(--color-text-border)] bg-white px-3 text-left text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]"
                >
                  <SelectValue placeholder={i18n.t("fsm.selectComplaintMode")} />
                </SelectTrigger>
                <SelectContent>
                  {COMPLAINT_MODES.map((mode) =>
                  <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.complaintMode &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.complaintMode}</p>
              }
              
            </div>

            {/* Complaint date */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="dateTime.complaintDate" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <input
                type="datetime-local"
                step="60"
                value={complaintDate}
                onChange={(e) => setComplaintDate(e.target.value)}
                className={inputCls(errors.complaintDate)} />
              
              {errors.complaintDate &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.complaintDate}</p>
              }
            </div>
            {/* Call Type */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.callType" /> 
                <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select value={callType} onValueChange={setCallType}>
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]">
                  <SelectValue placeholder={i18n.t("fsm.selectCallType")} />
                </SelectTrigger>
                <SelectContent>
                  {CALL_TYPES.map((type) =>
                  <SelectItem key={type} value={type}>{formatCallTypeLabel(type)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* System */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="inventory.product" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select
                value={selectedSystem?.id ?? ''}
                onValueChange={(id) => {
                  const full = systems.find((s) => s.id === id) ?? null;
                  setSelectedSystem(full);
                }}
                disabled={systemsLoading}>
                
                <SelectTrigger className={cn(
                  'h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]',
                  errors.system && 'border-[var(--color-error)]'
                )}>
                  <SelectValue placeholder={systemsLoading ? 'Loading products...' : systemsError || 'Select a product'} />
                </SelectTrigger>
                <SelectContent>
                  {systems.map((sys) =>
                  <SelectItem key={sys.id} value={sys.id}>{sys.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.system &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.system}</p>
              }
            </div>

            {/* Type of complaint */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.typeOfComplaint" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select
                value={selectedComplaint?.id ?? ''}
                onValueChange={(id) => {
                  const full = complaintTypes.find((c) => c.id === id) ?? null;
                  setSelectedComplaint(full);
                }}
                disabled={
                  !selectedSystem ||
                  complaintsLoading ||
                  Boolean(selectedSystem && !complaintsLoading && complaintTypes.length === 0)
                }>
                
                <SelectTrigger className={cn(
                  'h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]',
                  errors.complaintType && 'border-[var(--color-error)]'
                )}>
                  <SelectValue
                    placeholder={
                    !selectedSystem ?
                    'Choose a product first' :
                    complaintsLoading ?
                    'Loading complaint types...' :
                    complaintsError ?
                    complaintsError :
                    complaintTypes.length === 0 ?
                    i18n.t('fsm.noComplaintTypes') :
                    'Choose a complaint type'
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  {complaintTypes.map((type) =>
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.complaintType &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.complaintType}</p>
              }
            </div>

            {/* Checklist Template */}
            {/* <div className="flex flex-col">
               <label className={labelCls}>Checklist template</label>
               <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId} disabled={!selectedSystem}>
                 <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]">
                   <SelectValue placeholder={selectedSystem ? 'Select checklist template' : 'Choose a system first'} />
                 </SelectTrigger>
                 <SelectContent>
                   {filteredTemplates.map(t => (
                     <SelectItem key={t.id} value={t.id}>
                       {t.type} &middot; {t.complaintType} ({t.steps.length} steps)
                     </SelectItem>
                   ))}
                 </SelectContent>
               </Select>
              </div> */}

            {/* Priority */}
            <div className="flex flex-col">
              <label className={labelCls}>
                <Trans i18nKey="fsm.priority" /> <span className="text-[var(--color-error)]">*</span>
              </label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className={cn(
                  'h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]',
                  errors.priority && 'border-[var(--color-error)]'
                )}>
                  <SelectValue placeholder={i18n.t("fsm.setPriority")} />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((pri) =>
                  <SelectItem key={pri} value={pri}>
                      {translateTicketPriority(pri, i18n.t.bind(i18n))}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
              {errors.priority &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.priority}</p>
              }
            </div>

            

            {/* Custom complaint type */}
            {isOthersComplaint &&
            <div className="flex flex-col sm:col-span-3 animate-in fade-in duration-200">
                <label className={labelCls}>
                  <Trans i18nKey="fsm.customComplaintType" /> <span className="text-[var(--color-error)]">*</span>
                </label>
                <input
                type="text"
                placeholder={i18n.t("fsm.specifyTheCustomComplaintType")}
                value={customComplaintType}
                onChange={(e) => setCustomComplaintType(e.target.value)}
                className={inputCls(errors.customComplaintType)} />
              
                {errors.customComplaintType &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.customComplaintType}</p>
              }
              </div>
            }

            {/* Detailed description */}
            <div className="flex flex-col sm:col-span-3">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label className={labelCls}>
                  <Trans i18nKey="fsm.detailedDescription" /> <span className="text-[var(--color-error)]">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="hidden sm:inline text-[11px] text-gray-400 font-medium">
                    <Trans i18nKey="fsm.holdMicToDictate" />
                  </span>
                  {/* <VoiceMicButton
                    title={i18n.t("fsm.holdMicToDictate")}
                    onTranscript={(text) => {
                      setDescription((prev) => {
                        const next = String(text || "").trim();
                        if (!next) return prev;
                        const existing = String(prev || "").trim();
                        return existing ? `${existing} ${next}` : next;
                      });
                      setErrors((prev) => {
                        if (!prev.description) return prev;
                        const { description: _removed, ...rest } = prev;
                        return rest;
                      });
                    }}
                    onError={(message) => {
                      setToast({ type: "error", message });
                    }}
                  /> */}
                </div>
              </div>
              <textarea
                placeholder={i18n.t("fsm.describeTheFaultAsReportedByTheCustomer")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className={cn(
                  "w-full px-3 py-2 border rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/15 transition resize-vertical",
                  errors.description ?
                  "border-[var(--color-error)] focus:border-[var(--color-error)]" :
                  "border-[var(--color-text-border)] focus:border-[var(--color-accent)]"
                )} />
              {errors.description &&
              <p className="text-xs text-[var(--color-error)] font-medium mt-1">{errors.description}</p>
              }
            </div>

          </div>
        </div>

        {/* ── SECTION 3: Product, Warranty & AMC ───────────────────────────── */}
        <div className={sectionCard}>
          <div className="flex items-center gap-3 mb-5 border-b border-gray-100 pb-3">
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[var(--color-workspace)] text-[var(--color-primary)] font-bold text-xs">
              3
            </span>
            <div>
              <h2 className="text-[15px] font-bold text-foreground"><Trans i18nKey="fsm.assetWarrantyAmc" /></h2>
              {/* <p className="text-[11px] text-gray-400 font-medium">
                 As shared by the customer — the manager verifies against records.
                </p> */}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {/* Product dropdown */}
            <div className="flex flex-col">
              <label className={labelCls}><Trans i18nKey="common.asset" /></label>
              <Select
                value={selectedProduct?.id ?? ''}
                onValueChange={(id) => {
                  setSelectedProduct(products.find((p) => p.id === id) ?? null);
                }}
                disabled={!selectedCustomer || !selectedLocation || productsLoading}>
                
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]">
                  <SelectValue
                    placeholder={
                    !selectedCustomer ?
                    'Choose a customer first' :
                    !selectedLocation ?
                    'Choose a location first' :
                    productsLoading ?
                    'Loading assets...' :
                    productsError || 'Select asset'
                    } />
                  
                </SelectTrigger>
                <SelectContent>
                  {products.map((prod) =>
                  <SelectItem key={prod.id} value={prod.id}>{prod.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Warranty dropdown */}
            <div className="flex flex-col">
              <label className={labelCls}>Warranty</label>
              <Select value={warrantyType} onValueChange={setWarrantyType}>
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]">
                  <SelectValue placeholder="Select warranty" />
                </SelectTrigger>
                <SelectContent>
                  {WARRANTY_STATUSES.map((status) =>
                  <SelectItem key={status} value={status}>{status}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* AMC dropdown */}
            <div className="flex flex-col">
              <label className={labelCls}>AMC</label>
              <Select value={amcType} onValueChange={setAmcType}>
                <SelectTrigger className="h-10 rounded-xl border-[var(--color-text-border)] bg-white text-sm focus:ring-[var(--color-accent)]/15 focus:border-[var(--color-accent)]">
                  <SelectValue placeholder="Select AMC" />
                </SelectTrigger>
                <SelectContent>
                  {AMC_STATUSES.map((status) =>
                  <SelectItem key={status} value={status}>{formatCallTypeLabel(status)}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex items-center gap-3 justify-end mb-8">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={submitting}
            className="h-10 px-6 rounded-xl border-gray-200 text-gray-700 font-semibold text-sm hover:bg-gray-50 bg-white">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <button
            type="submit"
            disabled={submitting}
            className={cn(ui.primaryBtn, "h-10 px-6")}>
            
            {submitting ? 'Submitting…' : 'Submit Ticket'}
          </button>
        </div>

      </form>
    </AppLayout>);

}
